// ============================================================================
// RESOURCES.JS — Resource definitions & inventory management for Palm Island
// ============================================================================
import { getGameState, saveGameState } from './state.js';
import { eventBus, GameEvents } from './events.js';
import { RESOURCES } from './balance.js';

// ---------------------------------------------------------------------------
// Resource definitions
// ---------------------------------------------------------------------------
// Stack limits and drop rates are pulled from balance.js (RESOURCES.STACK_LIMITS
// and RESOURCES.DROP_RATES) where a matching constant exists, so that game
// designers only need to tweak one file.
// ---------------------------------------------------------------------------
export const RESOURCE_DEFS = {
  coconut: {
    name: 'Coconut',
    icon: '\u{1F965}',
    source: 'Palm tree shake',
    dropRate: RESOURCES.DROP_RATES.coconut_per_tree ?? 3,
    stackLimit: RESOURCES.STACK_LIMITS.coconut ?? 50,
    spawnTimer: 90,
    maxVisible: 6,
    description: 'Falls from palm trees when shaken',
  },
  coconut_shell: {
    name: 'Coconut Shell',
    icon: '\u{1F963}',
    source: 'Cracking coconut',
    dropRate: 2,
    stackLimit: RESOURCES.STACK_LIMITS.coconut_shell ?? 99,
    spawnTimer: null,
    maxVisible: 8,
    description: 'Crack a coconut to get two halves',
  },
  gem: {
    name: 'Gem',
    icon: '\u{1F48E}',
    source: 'Treasure chest',
    dropRate: RESOURCES.DROP_RATES.gem_per_click ?? 1,
    stackLimit: RESOURCES.STACK_LIMITS.gem ?? 99,
    spawnTimer: null,
    maxVisible: 3,
    description: 'Sparkling gems from the treasure chest',
  },
  gold_coin: {
    name: 'Gold Coin',
    icon: '\u{1FA99}',
    source: 'Treasure chest',
    dropRate: 1,
    stackLimit: RESOURCES.STACK_LIMITS.gold_coin ?? 99,
    spawnTimer: null,
    maxVisible: 5,
    description: 'Shiny coins from the treasure chest',
  },
  seashell: {
    name: 'Seashell',
    icon: '\u{1F41A}',
    source: 'Beach spawn',
    dropRate: 1,
    stackLimit: RESOURCES.STACK_LIMITS.seashell ?? 99,
    spawnTimer: 60,
    maxVisible: 4,
    description: 'Washes up on the shore periodically',
  },
  glass_shard: {
    name: 'Glass Shard',
    icon: '\u{1F539}',
    source: 'Bottle recycling',
    dropRate: 1,
    stackLimit: RESOURCES.STACK_LIMITS.glass_shard ?? 99,
    spawnTimer: null,
    maxVisible: 3,
    description: 'Recycled from collected bottles',
  },
  driftwood: {
    name: 'Driftwood',
    icon: '\u{1FAB5}',
    source: 'Water edge spawn',
    dropRate: 1,
    stackLimit: RESOURCES.STACK_LIMITS.driftwood ?? 50,
    spawnTimer: 45,
    maxVisible: 4,
    description: 'Washes ashore from the ocean',
  },
  fish: {
    name: 'Fish',
    icon: '\u{1F41F}',
    source: 'Seagull dive',
    dropRate: RESOURCES.DROP_RATES.fish_dive_chance ?? 0.3,
    stackLimit: RESOURCES.STACK_LIMITS.fish ?? 30,
    spawnTimer: null,
    maxVisible: 2,
    description: 'Dropped by diving seagulls',
  },
  melody_fragment: {
    name: 'Melody Fragment',
    icon: '\u{1F3B5}',
    source: 'Music note catching',
    dropRate: 1,
    stackLimit: RESOURCES.STACK_LIMITS.melody_fragment ?? 20,
    spawnTimer: null,
    maxVisible: 3,
    description: 'Caught from floating music notes',
  },
  sand_dollar: {
    name: 'Sand Dollar',
    icon: '\u2B50',
    source: 'Tide discovery',
    dropRate: 1,
    stackLimit: RESOURCES.STACK_LIMITS.sand_dollar ?? 50,
    spawnTimer: 150,
    maxVisible: 2,
    description: 'Rare find near the waterline',
  },
};

// ---------------------------------------------------------------------------
// Inventory management
// ---------------------------------------------------------------------------

/**
 * Add resource to inventory, respecting stack limits.
 * @param {string} resourceType — key in RESOURCE_DEFS
 * @param {number} [amount=1]
 * @returns {boolean} true if at least one unit was added
 */
export function addToInventory(resourceType, amount = 1) {
  const def = RESOURCE_DEFS[resourceType];
  if (!def) {
    console.warn(`[Resources] Unknown resource: ${resourceType}`);
    return false;
  }

  const state = getGameState();
  const current = state.inventory[resourceType] || 0;
  const newAmount = Math.min(current + amount, def.stackLimit);
  const added = newAmount - current;

  if (added <= 0) return false; // stack full

  state.inventory[resourceType] = newAmount;
  saveGameState(state);

  eventBus.emit(GameEvents.INVENTORY_CHANGED, {
    resourceType,
    amount: added,
    total: newAmount,
    action: 'add',
  });

  return true;
}

/**
 * Remove resource from inventory (for crafting / spending).
 * @param {string} resourceType — key in RESOURCE_DEFS
 * @param {number} [amount=1]
 * @returns {boolean} true if removal succeeded
 */
export function removeFromInventory(resourceType, amount = 1) {
  const state = getGameState();
  const current = state.inventory[resourceType] || 0;

  if (current < amount) return false; // not enough

  state.inventory[resourceType] = current - amount;
  saveGameState(state);

  eventBus.emit(GameEvents.INVENTORY_CHANGED, {
    resourceType,
    amount: -amount,
    total: current - amount,
    action: 'remove',
  });

  return true;
}

/**
 * Get a shallow copy of the full inventory.
 * @returns {Record<string, number>}
 */
export function getInventory() {
  return { ...getGameState().inventory };
}

/**
 * Check if player can afford a cost map.
 * @param {Record<string, number>} costMap — e.g. { driftwood: 3, coconut_shell: 5 }
 * @returns {boolean}
 */
export function canAfford(costMap) {
  const inv = getGameState().inventory;
  for (const [type, needed] of Object.entries(costMap)) {
    if ((inv[type] || 0) < needed) return false;
  }
  return true;
}

/**
 * Spend resources according to a cost map (atomic — all or nothing).
 * @param {Record<string, number>} costMap
 * @returns {boolean} true if spend succeeded
 */
export function spendResources(costMap) {
  if (!canAfford(costMap)) return false;
  for (const [type, amount] of Object.entries(costMap)) {
    removeFromInventory(type, amount);
  }
  return true;
}
